import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Card } from '../components/common/Card';
import { Button } from '../components/common/Button';
import { Select } from '../components/common/Select';
import { attendanceService } from '../services/attendanceService';
import { payrollService } from '../services/payrollService';
import { leaveService } from '../services/leaveService';
import { employeeService } from '../services/employeeService';
import { Employee, AttendanceSummary, PayrollRecord, LeaveRequest, LeaveBalance } from '../types';
import { useAuth } from '../context/AuthContext';

const monthOptions = [
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const currentYear = new Date().getFullYear();
// Generate year options: next year, current year, and previous years
// This will include 2027 if current year is 2026, and will dynamically adjust
const generateYearOptions = (baseYear: number = currentYear) => {
  const years = [];
  // Add next year (e.g., 2027 if base is 2026)
  years.push({ value: String(baseYear + 1), label: String(baseYear + 1) });
  // Add current/base year
  years.push({ value: String(baseYear), label: String(baseYear) });
  // Add previous 4 years
  for (let i = 1; i <= 4; i++) {
    years.push({ value: String(baseYear - i), label: String(baseYear - i) });
  }
  return years.sort((a, b) => parseInt(b.value) - parseInt(a.value)); // Sort descending
};

export function ReportsPage() {
  const { user } = useAuth();
  const [selectedMonth, setSelectedMonth] = useState(String(new Date().getMonth() + 1));
  const [selectedYear, setSelectedYear] = useState(String(currentYear));
  const [loading, setLoading] = useState(true);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [attendanceData, setAttendanceData] = useState<Record<number, AttendanceSummary>>({});
  const [payrollData, setPayrollData] = useState<PayrollRecord[]>([]);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [leaveBalances, setLeaveBalances] = useState<Record<number, LeaveBalance>>({});
  const [activeTab, setActiveTab] = useState<'attendance' | 'payroll' | 'leave'>('attendance');

  const isAdmin = user?.role === 'ADMIN' || user?.role === 'HR';

  useEffect(() => {
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin && employees.length > 0) {
      fetchReportData();
    }
  }, [selectedMonth, selectedYear, activeTab, isAdmin]);

  const fetchEmployees = async () => {
    try {
      // Fetch all employees by using a large limit
      const response = await employeeService.getAll(1, 1000);
      setEmployees(response.data);
      
      // If there are more pages, fetch them
      if (response.pagination && response.pagination.totalPages > 1) {
        const allEmployees = [...response.data];
        for (let page = 2; page <= response.pagination.totalPages; page++) {
          const nextResponse = await employeeService.getAll(page, 1000);
          allEmployees.push(...nextResponse.data);
        }
        setEmployees(allEmployees);
      }
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Failed to load employees');
    }
  };

  const fetchReportData = async () => {
    try {
      setLoading(true);
      const month = parseInt(selectedMonth);
      const year = parseInt(selectedYear);
      const employeeId = undefined; // Always show all employees

      // Fetch attendance data
      if (activeTab === 'attendance' || activeTab === 'leave') {
        if (employeeId) {
          try {
            const summary = await attendanceService.getAttendanceSummary(month, year, employeeId);
            setAttendanceData({ [employeeId]: summary });
          } catch {
            setAttendanceData({});
          }
        } else {
          // Use already fetched employees
          const employeesToProcess = employees.length > 0 ? employees : [];
          const attendancePromises = employeesToProcess.map(async (emp) => {
        try {
          const summary = await attendanceService.getAttendanceSummary(month, year, emp.id);
          return { employeeId: emp.id, summary };
        } catch {
          return { employeeId: emp.id, summary: null };
        }
      });

      const attendanceResults = await Promise.all(attendancePromises);
      const attendanceMap: Record<number, AttendanceSummary> = {};
      attendanceResults.forEach(({ employeeId, summary }) => {
        if (summary) {
          attendanceMap[employeeId] = summary;
        }
      });
      setAttendanceData(attendanceMap);
        }
      }

      // Fetch payroll data
      if (activeTab === 'payroll') {
      try {
        const payroll = await payrollService.getPayrollRecords(month, year);
          const filteredPayroll = employeeId 
            ? payroll.filter(p => p.employeeId === employeeId)
            : payroll;
          setPayrollData(filteredPayroll);
      } catch {
        setPayrollData([]);
        }
      }

      // Fetch leave data
      if (activeTab === 'leave') {
        try {
          // Fetch leave requests
          const requests = await leaveService.getAllLeaveRequests();
          const filteredRequests = employeeId
            ? requests.filter(r => r.employeeId === employeeId)
            : requests;
          
          // Filter by month/year
          const monthRequests = filteredRequests.filter(req => {
            const reqDate = new Date(req.startDate);
            return reqDate.getMonth() + 1 === month && reqDate.getFullYear() === year;
          });
          setLeaveRequests(monthRequests);

          // Fetch leave balances for all employees
          if (employeeId) {
            try {
              const balance = await leaveService.getEmployeeLeaveBalance(employeeId, month, year);
              setLeaveBalances({ [employeeId]: balance });
            } catch {
              setLeaveBalances({});
            }
          } else {
            // Use already fetched employees
            const employeesToProcess = employees.length > 0 ? employees : [];
            const balancePromises = employeesToProcess.map(async (emp) => {
              try {
                const balance = await leaveService.getEmployeeLeaveBalance(emp.id, month, year);
                return { employeeId: emp.id, balance };
              } catch {
                return { employeeId: emp.id, balance: null };
              }
            });

            const balanceResults = await Promise.all(balancePromises);
            const balanceMap: Record<number, LeaveBalance> = {};
            balanceResults.forEach(({ employeeId, balance }) => {
              if (balance) {
                balanceMap[employeeId] = balance;
              }
            });
            setLeaveBalances(balanceMap);
          }
        } catch (error) {
          console.error('Error fetching leave data:', error);
          setLeaveRequests([]);
          setLeaveBalances({});
        }
      }
    } catch (error) {
      console.error('Error fetching report data:', error);
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  const exportAttendanceCSV = async () => {
    try {
      const month = parseInt(selectedMonth);
      const year = parseInt(selectedYear);
      const blob = await attendanceService.exportToCsv(month, year);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `attendance_report_${month}_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Attendance report downloaded');
    } catch (error) {
      console.error('Error exporting attendance:', error);
      toast.error('Failed to export attendance');
    }
  };

  const exportPayrollCSV = async () => {
    try {
      const month = parseInt(selectedMonth);
      const year = parseInt(selectedYear);
      const blob = await payrollService.exportToCsv(month, year);
      
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `payroll_report_${month}_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Payroll report downloaded');
    } catch (error) {
      console.error('Error exporting payroll:', error);
      toast.error('Failed to export payroll');
    }
  };

  const exportLeaveCSV = () => {
    try {
      const year = parseInt(selectedYear);
      const monthName = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;
      
      // Create CSV content
      const headers = ['Employee Code', 'Employee Name', 'Department', 'Leave Type', 'Start Date', 'End Date', 'Days', 'Status', 'Reason'];
      const rows = leaveRequests.map(req => {
        const emp = employees.find(e => e.id === req.employeeId);
        const startDate = new Date(req.startDate);
        const endDate = new Date(req.endDate);
        const days = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
        
        return [
          emp?.employeeCode || '',
          emp ? `${emp.firstName} ${emp.lastName}` : '',
          emp?.department || '',
          req.leaveType,
          req.startDate,
          req.endDate,
          req.isHalfDay ? '0.5' : String(days),
          req.status,
          req.reason || '',
        ];
      });

      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `leave_report_${monthName}_${year}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      
      toast.success('Leave report downloaded');
    } catch (error) {
      console.error('Error exporting leave:', error);
      toast.error('Failed to export leave report');
    }
  };

  const formatCurrency = (amount: number | string) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(Number(amount) || 0);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  // Calculate totals - always show all employees
  const filteredEmployees = employees;

  const totalPresent = Object.values(attendanceData).reduce((sum, d) => sum + (d?.present || 0), 0);
  const totalPresentLate = Object.values(attendanceData).reduce((sum, d) => sum + (d?.presentLate || 0), 0);
  const totalAbsent = Object.values(attendanceData).reduce((sum, d) => sum + (d?.absent || 0), 0);
  const totalLeaves = Object.values(attendanceData).reduce(
    (sum, d) => sum + (d?.leaveCL || 0) + (d?.leavePL || 0),
    0
  );
  const totalHalfDays = Object.values(attendanceData).reduce((sum, d) => sum + (d?.halfDay || 0), 0);

  const totalGrossSalary = payrollData.reduce((sum, p) => sum + Number(p.grossSalary || 0), 0);
  const totalDeductions = payrollData.reduce((sum, p) => sum + Number(p.totalDeductions || 0), 0);
  const totalNetSalary = payrollData.reduce((sum, p) => sum + Number(p.netSalary || 0), 0);

  // Leave statistics
  const approvedLeaves = leaveRequests.filter(r => r.status === 'APPROVED').length;
  const pendingLeaves = leaveRequests.filter(r => r.status === 'PENDING').length;
  const totalCLUsed = Object.values(leaveBalances).reduce((sum, b) => sum + (b?.casualLeave?.used || 0), 0);

  if (!isAdmin) {
    return (
      <div className="animate-in">
        <Card className="p-8 text-center">
          <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Access Restricted</h2>
          <p className="text-gray-500">Reports are only available for Admin and HR users.</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="animate-in space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Reports & Analytics</h1>
          <p className="text-gray-500 mt-1">Comprehensive reports for attendance, payroll, and leave management</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Month:</label>
          <Select
            options={monthOptions}
            value={selectedMonth}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedMonth(e.target.value)}
          />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Year:</label>
          <Select
              options={generateYearOptions(currentYear)}
            value={selectedYear}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setSelectedYear(e.target.value)}
          />
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Employees</p>
              <p className="text-2xl font-bold text-gray-900">{filteredEmployees.length}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
          </div>
        </Card>

        {activeTab === 'attendance' && (
          <>
        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Present Days</p>
              <p className="text-2xl font-bold text-green-600">{totalPresent}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Leave Days</p>
              <p className="text-2xl font-bold text-yellow-600">{totalLeaves}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
          </div>
        </Card>

        <Card className="p-4">
          <div className="flex items-center justify-between">
            <div>
                  <p className="text-sm text-gray-500">Total Absent Days</p>
                  <p className="text-2xl font-bold text-red-600">{totalAbsent}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>
            </Card>
          </>
        )}

        {activeTab === 'payroll' && (
          <>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Gross Salary</p>
                  <p className="text-2xl font-bold text-primary-600">{formatCurrency(totalGrossSalary)}</p>
            </div>
            <div className="p-3 bg-primary-100 rounded-full">
              <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
          </div>
        </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Deductions</p>
                  <p className="text-2xl font-bold text-red-600">{formatCurrency(totalDeductions)}</p>
                </div>
                <div className="p-3 bg-red-100 rounded-full">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Net Salary</p>
                  <p className="text-2xl font-bold text-green-600">{formatCurrency(totalNetSalary)}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </Card>
          </>
        )}

        {activeTab === 'leave' && (
          <>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Approved Leaves</p>
                  <p className="text-2xl font-bold text-green-600">{approvedLeaves}</p>
                </div>
                <div className="p-3 bg-green-100 rounded-full">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Pending Leaves</p>
                  <p className="text-2xl font-bold text-yellow-600">{pendingLeaves}</p>
                </div>
                <div className="p-3 bg-yellow-100 rounded-full">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </Card>

            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total CL Used</p>
                  <p className="text-2xl font-bold text-blue-600">{totalCLUsed.toFixed(1)}</p>
                </div>
                <div className="p-3 bg-blue-100 rounded-full">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 overflow-x-auto">
        <nav className="-mb-px flex gap-4 sm:gap-6 min-w-max sm:min-w-0">
          {(['attendance', 'payroll', 'leave'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-2 px-1 border-b-2 font-medium text-xs sm:text-sm capitalize transition-colors whitespace-nowrap ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab} Report
            </button>
          ))}
        </nav>
      </div>

      {/* Report Content */}
      {loading ? (
        <Card className="p-8">
          <div className="flex items-center justify-center">
            <div className="spinner w-8 h-8 border-4 border-primary-600 border-t-transparent" />
          </div>
        </Card>
      ) : (
        <>
          {/* Attendance Report */}
          {activeTab === 'attendance' && (
            <Card className="overflow-hidden">
              <div className="p-3 sm:p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                  Attendance Report - {monthOptions.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </h3>
                <Button variant="outline" size="sm" onClick={exportAttendanceCSV} className="w-full sm:w-auto">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </Button>
              </div>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Present</th>
                        <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Present Late</th>
                        <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Absent</th>
                        <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Half Day</th>
                        <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">CL</th>
                        <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">PL</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredEmployees.map((emp) => {
                        const data = attendanceData[emp.id];
                        return (
                          <tr key={emp.id} className="hover:bg-gray-50">
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 ${
                                  emp.gender === 'F' ? 'bg-pink-500' : 'bg-blue-500'
                                }`}>
                                  {emp.firstName[0]}{emp.lastName[0]}
                                </div>
                                <div className="ml-2 sm:ml-3 min-w-0">
                                  <div className="text-sm font-medium text-gray-900 truncate">{emp.firstName} {emp.lastName}</div>
                                  <div className="text-xs text-gray-500 truncate">{emp.employeeCode}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-2 sm:px-6 py-4 text-center text-sm text-green-600 font-medium">{data?.present || 0}</td>
                            <td className="px-2 sm:px-6 py-4 text-center text-sm text-yellow-500 font-medium">{data?.presentLate || 0}</td>
                            <td className="px-2 sm:px-6 py-4 text-center text-sm text-red-600 font-medium">{data?.absent || 0}</td>
                            <td className="px-2 sm:px-6 py-4 text-center text-sm text-yellow-600 font-medium">{data?.halfDay || 0}</td>
                            <td className="px-2 sm:px-6 py-4 text-center text-sm text-blue-600 font-medium">{data?.leaveCL || 0}</td>
                            <td className="px-2 sm:px-6 py-4 text-center text-sm text-purple-600 font-medium">{data?.leavePL || 0}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    {filteredEmployees.length > 0 && (
                    <tfoot className="bg-gray-50">
                      <tr>
                        <td className="px-3 sm:px-6 py-3 text-sm font-semibold text-gray-900">Total</td>
                        <td className="px-2 sm:px-6 py-3 text-center text-sm font-semibold text-green-600">{totalPresent}</td>
                        <td className="px-2 sm:px-6 py-3 text-center text-sm font-semibold text-yellow-500">{totalPresentLate}</td>
                        <td className="px-2 sm:px-6 py-3 text-center text-sm font-semibold text-red-600">{totalAbsent}</td>
                        <td className="px-2 sm:px-6 py-3 text-center text-sm font-semibold text-yellow-600">{totalHalfDays}</td>
                        <td className="px-2 sm:px-6 py-3 text-center text-sm font-semibold text-blue-600">
                          {Object.values(attendanceData).reduce((sum, d) => sum + (d?.leaveCL || 0), 0)}
                        </td>
                        <td className="px-2 sm:px-6 py-3 text-center text-sm font-semibold text-purple-600">
                          {Object.values(attendanceData).reduce((sum, d) => sum + (d?.leavePL || 0), 0)}
                        </td>
                        <td className="px-2 sm:px-6 py-3 text-center text-sm font-semibold text-gray-600">
                            {Object.values(attendanceData).reduce((sum) => sum, 0)}
                        </td>
                      </tr>
                    </tfoot>
                    )}
                  </table>
                </div>
              </div>
            </Card>
          )}

          {/* Payroll Report */}
          {activeTab === 'payroll' && (
            <Card className="overflow-hidden">
              <div className="p-3 sm:p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                  Payroll Report - {monthOptions.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </h3>
                <Button variant="outline" size="sm" onClick={exportPayrollCSV} className="w-full sm:w-auto">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </Button>
              </div>
              {payrollData.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p>No payroll data available for this period.</p>
                  <p className="text-sm mt-2">Run payroll from the Payroll page to generate records.</p>
                </div>
              ) : (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <div className="inline-block min-w-full align-middle">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                          <th className="px-2 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Working Days</th>
                          <th className="px-2 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Present</th>
                          <th className="px-2 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Gross Salary</th>
                          <th className="px-2 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Deductions</th>
                          <th className="px-2 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Net Salary</th>
                          <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {payrollData.map((record) => (
                          <tr key={record.id} className="hover:bg-gray-50">
                            <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {record.employee?.firstName} {record.employee?.lastName}
                              </div>
                              <div className="text-xs text-gray-500 truncate">{record.employee?.employeeCode}</div>
                            </td>
                            <td className="px-2 sm:px-6 py-4 text-right text-sm text-gray-900">{record.workingDays}</td>
                            <td className="px-2 sm:px-6 py-4 text-right text-sm text-gray-900">{Number(record.presentDays).toFixed(1)}</td>
                            <td className="px-2 sm:px-6 py-4 text-right text-sm text-gray-900">{formatCurrency(record.grossSalary)}</td>
                            <td className="px-2 sm:px-6 py-4 text-right text-sm text-red-600">{formatCurrency(record.totalDeductions)}</td>
                            <td className="px-2 sm:px-6 py-4 text-right text-sm font-semibold text-green-600">{formatCurrency(record.netSalary)}</td>
                            <td className="px-2 sm:px-6 py-4 text-center">
                              <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                record.status === 'PAID' ? 'bg-green-100 text-green-800' :
                                record.status === 'PROCESSED' ? 'bg-blue-100 text-blue-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {record.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="bg-gray-50">
                        <tr>
                          <td className="px-3 sm:px-6 py-3 text-sm font-semibold text-gray-900" colSpan={3}>Total</td>
                          <td className="px-2 sm:px-6 py-3 text-right text-sm font-semibold text-gray-900">{formatCurrency(totalGrossSalary)}</td>
                          <td className="px-2 sm:px-6 py-3 text-right text-sm font-semibold text-red-600">{formatCurrency(totalDeductions)}</td>
                          <td className="px-2 sm:px-6 py-3 text-right text-sm font-semibold text-green-600">{formatCurrency(totalNetSalary)}</td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </Card>
          )}

          {/* Leave Report */}
          {activeTab === 'leave' && (
            <Card className="overflow-hidden">
              <div className="p-3 sm:p-4 border-b border-gray-200 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                <h3 className="font-semibold text-gray-900 text-sm sm:text-base">
                  Leave Report - {monthOptions.find(m => m.value === selectedMonth)?.label} {selectedYear}
                </h3>
                <Button variant="outline" size="sm" onClick={exportLeaveCSV} className="w-full sm:w-auto">
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export CSV
                </Button>
              </div>
              
              {/* Leave Requests Table */}
              <div className="p-4 border-b border-gray-200">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Leave Requests</h4>
              </div>
              <div className="overflow-x-auto -mx-4 sm:mx-0">
                <div className="inline-block min-w-full align-middle">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                        <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leave Type</th>
                        <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Start Date</th>
                        <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">End Date</th>
                        <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Days</th>
                        <th className="px-2 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reason</th>
                        <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {leaveRequests.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                            No leave requests found for this period.
                          </td>
                        </tr>
                      ) : (
                        leaveRequests.map((request) => {
                          const emp = employees.find(e => e.id === request.employeeId);
                          const startDate = new Date(request.startDate);
                          const endDate = new Date(request.endDate);
                          const days = request.isHalfDay ? 0.5 : Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
                          
                          return (
                            <tr key={request.id} className="hover:bg-gray-50">
                              <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 ${
                                    emp?.gender === 'F' ? 'bg-pink-500' : 'bg-blue-500'
                                  }`}>
                                    {emp ? `${emp.firstName[0]}${emp.lastName[0]}` : '?'}
                                  </div>
                                  <div className="ml-2 sm:ml-3 min-w-0">
                                    <div className="text-sm font-medium text-gray-900 truncate">
                                      {emp ? `${emp.firstName} ${emp.lastName}` : 'Unknown'}
                                    </div>
                                    <div className="text-xs text-gray-500 truncate">{emp?.employeeCode || ''}</div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-2 sm:px-6 py-4 whitespace-nowrap">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  request.leaveType === 'CL' ? 'bg-blue-100 text-blue-800' :
                                  request.leaveType === 'PL' ? 'bg-purple-100 text-purple-800' :
                                  'bg-orange-100 text-orange-800'
                                }`}>
                                  {request.leaveType}
                                </span>
                              </td>
                              <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(request.startDate)}</td>
                              <td className="px-2 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatDate(request.endDate)}</td>
                              <td className="px-2 sm:px-6 py-4 text-center text-sm text-gray-900">{days}</td>
                              <td className="px-2 sm:px-6 py-4 text-sm text-gray-500 max-w-xs truncate">{request.reason || '-'}</td>
                              <td className="px-2 sm:px-6 py-4 text-center">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                                  request.status === 'APPROVED' ? 'bg-green-100 text-green-800' :
                                  request.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                                  request.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {request.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Leave Balances Table */}
              {Object.keys(leaveBalances).length > 0 && (
                <>
                  <div className="p-4 border-t border-gray-200">
                    <h4 className="text-sm font-semibold text-gray-700 mb-3">Leave Balances</h4>
                  </div>
                  <div className="overflow-x-auto -mx-4 sm:mx-0">
                    <div className="inline-block min-w-full align-middle">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                            <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Employee</th>
                            <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">CL Entitled</th>
                            <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">CL Used</th>
                            <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">CL Balance</th>
                            <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">PL Entitled</th>
                            <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">PL Used</th>
                            <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">PL Balance</th>
                            <th className="px-2 sm:px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Unpaid Used</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                          {filteredEmployees.map((emp) => {
                            const balance = leaveBalances[emp.id];
                            if (!balance) return null;
                            
                      return (
                        <tr key={emp.id} className="hover:bg-gray-50">
                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 ${
                                emp.gender === 'F' ? 'bg-pink-500' : 'bg-blue-500'
                              }`}>
                                {emp.firstName[0]}{emp.lastName[0]}
                              </div>
                                    <div className="ml-2 sm:ml-3 min-w-0">
                                      <div className="text-sm font-medium text-gray-900 truncate">{emp.firstName} {emp.lastName}</div>
                                      <div className="text-xs text-gray-500 truncate">{emp.employeeCode}</div>
                              </div>
                            </div>
                          </td>
                                <td className="px-2 sm:px-6 py-4 text-center text-sm text-gray-900">{balance.casualLeave?.entitled || 0}</td>
                                <td className="px-2 sm:px-6 py-4 text-center text-sm text-blue-600 font-medium">{balance.casualLeave?.used || 0}</td>
                                <td className="px-2 sm:px-6 py-4 text-center text-sm text-green-600 font-medium">{balance.casualLeave?.balance || 0}</td>
                                <td className="px-2 sm:px-6 py-4 text-center text-sm text-gray-900">{balance.paidLeave?.entitled || 0}</td>
                                <td className="px-2 sm:px-6 py-4 text-center text-sm text-purple-600 font-medium">{balance.paidLeave?.used || 0}</td>
                                <td className="px-2 sm:px-6 py-4 text-center text-sm text-green-600 font-medium">{balance.paidLeave?.balance || 0}</td>
                                <td className="px-2 sm:px-6 py-4 text-center text-sm text-orange-600 font-medium">{balance.unpaidUsed || 0}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
                  </div>
                </>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
